import client from '../lib/client';
/**
 * Personal OAuth connect through the platform's V2 connector flow, the path the platform SPA uses
 * (`startOAuthFlowV2`). The platform removed the V1 `generateUrl` / `exchangeOAuthToken` endpoints
 * in v2.942.1. In the V2 flow the platform's own callback exchanges the code and stores the
 * credential, so tokens never reach the add-in.
 */
/**
 * The `provider` value `GET /v2/OAuth/providers` filters on for each credential type. Mirrors the
 * platform SPA's `oAuthProviderWithCredentialType`; a type missing here has no V2 connect path.
 */
const connectorProviderByCredentialType = {
    MicrosoftOauth: 'MicrosoftGraph',
    GoogleOauth: 'GoogleSuite',
    GenericOAuth: 'GenericOAuth',
    SalesforceOauth: 'Salesforce',
    AtlassianOauth: 'Atlassian',
    DropboxOauth: 'Dropbox',
    BoxOauth: 'Box',
    NotionOauth: 'Notion',
    SlackOauth: 'Slack',
};
/**
 * Connector authentication types with no interactive consent step: tokens are minted server to
 * server, or forwarded from the user's sign-in. Mirrors the platform SPA's
 * `hasNoInteractiveConnectFlow`.
 */
const nonInteractiveAuthenticationTypes = new Set([
    'StaticM2MOauth',
    'DynamicM2MOauth',
    'DynamicJwtAssertion',
    'StaticJwtAssertion',
    'StaticAwsStsExternalId',
    'ClerkM2MOauth',
    'TokenExchangeOauth',
    'PassThroughOauth',
    'IdJagCrossAppAccess',
]);
/**
 * The credential type a tool's Connect button mints, or undefined when the tool offers no personal
 * connect. Like the platform SPA, only tools that run with each user's own credential
 * (`userAuthEnabled`) get one, and only through a type with a V2 connector path.
 * `platformSupportedOAuthTypes` cannot decide this: the platform sends the same global list for
 * every tool.
 */
export const personalConnectCredentialType = (integration) => {
    if (integration.userAuthEnabled !== true) {
        return undefined;
    }
    return integration.credentialSettings?.availableCredentialTypes?.find((type) => type in connectorProviderByCredentialType);
};
/** No connector with a consent step is configured for the credential type. */
export class NoOAuthConnectorError extends Error {
    credentialType;
    constructor(credentialType) {
        super(`No OAuth connector with a sign-in step is configured for ${credentialType}`);
        this.credentialType = credentialType;
        this.name = 'NoOAuthConnectorError';
    }
}
const authHeaders = (accessToken) => ({
    headers: { Authorization: `Bearer ${accessToken}` },
});
/**
 * The connector a tool without a bound connector connects through: the tenant's own interactive
 * connector ahead of Airia's system one, then list order. Mirrors the platform SPA's
 * `pickDefaultInteractiveConnector`, so the add-in and the SPA mint from the same connector.
 */
export const pickDefaultConnector = (connectors) => {
    const interactive = connectors.filter((connector) => !nonInteractiveAuthenticationTypes.has(connector.authenticationType ?? ''));
    return interactive.find((connector) => !connector.isSystem) ?? interactive[0];
};
/**
 * The connector id to authorize through. A tool bound to a connector (`userCredentialConnectorId`)
 * always uses it; otherwise the default same-type connector is picked.
 */
export const resolveConnectorId = async (credentialType, boundConnectorId, accessToken) => {
    if (boundConnectorId) {
        return boundConnectorId;
    }
    const provider = connectorProviderByCredentialType[credentialType];
    if (!provider) {
        throw new NoOAuthConnectorError(credentialType);
    }
    const response = await client.get('/v2/OAuth/providers', {
        params: { provider },
        ...authHeaders(accessToken),
    });
    const connector = pickDefaultConnector(Array.isArray(response.data) ? response.data : []);
    if (!connector) {
        throw new NoOAuthConnectorError(credentialType);
    }
    return connector.id;
};
/**
 * Starts a personal connect and returns the consent URL to open. An empty scope list makes the
 * platform request every scope the connector offers.
 */
export const startConnectorAuthorization = async (connectorId, credentialName, accessToken) => {
    const response = await client.post(`/v2/OAuth/providers/${connectorId}/authorize`, { scopes: [], isPersonal: true, credentialName, metadata: {} }, authHeaders(accessToken));
    return response.data.redirectUrl;
};
/** Whether the user holds a connected credential minted through the connector. */
export const isConnectorConnected = async (connectorId, accessToken) => {
    const response = await client.get('/v1/UserOAuthProvider/status', authHeaders(accessToken));
    const match = response.data.items?.find((item) => item.providerId === connectorId);
    return match?.status === 'Connected' && Boolean(match.credentialId);
};
